// The schemas are a set of types, each associated with a set of properties.
// Schema are used to improve the way search engines read and represent our page.
// We use Schema in the JSON+LD format.
// We are using the follow pattern => Individual items: When each item is a separate block on the same page =>
// https://developers.google.com/search/docs/advanced/structured-data/sd-policies#individual-items
// You can read more at https://schema.org
import { getImage, IGatsbyImageData } from 'gatsby-plugin-image';

import config from '../siteConfig';

const schemaContext = 'https://schema.org';
const everlyLogo =
  'https://www.everlywell.com/images/logos/2019-rebrand/everlywell-logo-e3288d32.svg';

interface SchemaOrgWebsite {
  '@context': string;
  '@type': 'WebSite';
  url: string;
  name: string;
  alternateName: string;
}

interface SchemaWebPage {
  '@context': string;
  '@type': 'WebPage';
  url: string;
  name: string;
}

interface SchemaBreadcrumbListItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item: {
    '@id': string;
  };
}

interface SchemaBreadcrumbList {
  '@context': string;
  '@type': 'BreadcrumbList';
  itemListElement: SchemaBreadcrumbListItem[];
}

interface SchemaBlogPosting {
  '@context': string;
  '@type': 'BlogPosting';
  url: string;
  name: string;
  alternateName: string;
  headline: string;
  image: {
    '@type': 'ImageObject';
    url: string;
    width: number;
    height: number;
  };
  author: {
    '@type': 'Person';
    name: string;
    url: string;
  };
  publisher: {
    '@type': 'Organization';
    name: string;
    url: string;
    logo: string;
  };
  datePublished: string;
  mainEntityOfPage: string;
}

interface SchemaFAQ {
  '@context': string;
  '@type': 'FAQPage';
  mainEntity: Array<{
    '@type': 'Question';
    name: string;
    acceptedAnswer: {
      '@type': 'Answer';
      text: string;
    };
  }>;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface BlogPostSchemaData {
  title: string;
  slug: string;
  canonicalUrl?: string;
  publishDateISO: string;
  subcategories: Array<{ title: string; slug: string }>;
  heroImage: {
    gatsbyImageData: IGatsbyImageData;
  };
}

// WebSite Schema. This is the default schema and it will be loaded in all pages.
export const schemaOrgJSONLD: SchemaOrgWebsite[] = [
  {
    '@context': schemaContext,
    '@type': 'WebSite',
    url: config.siteUrl,
    name: config.siteTitle,
    alternateName: config.siteTitleAlt ? config.siteTitleAlt : '',
  },
];

// WebPage Schema.
export const schemaWebPageJSONLD = (
  url: string,
  name: string,
): SchemaWebPage[] => [
  {
    '@context': schemaContext,
    '@type': 'WebPage',
    url: url,
    name: name,
  },
];

// BreadcrumbList and BlogPosting (Articles) Schema.
export const schemaBreadcrumbAndPostJSONLD = (
  data: BlogPostSchemaData,
): [SchemaBreadcrumbList, SchemaBlogPosting] => {
  const url = `${config.siteUrl}/blog/${data.slug}`;
  const mainEntityOfPage = data.canonicalUrl
    ? `${config.siteUrl}/blog/${data.canonicalUrl}`
    : url;

  return [
    {
      '@context': schemaContext,
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': `${config.siteUrl}/blog`,
          },
          name: 'Blog',
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': `${config.siteUrl}/blog/${
              data.subcategories[0]?.slug || ''
            }`,
          },
          name: data.subcategories[0]?.title || '',
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@id': url,
          },
          name: data.title,
        },
      ],
    },
    {
      '@context': schemaContext,
      '@type': 'BlogPosting',
      url: url,
      name: data.title,
      alternateName: config.siteTitleAlt ? config.siteTitleAlt : '',
      headline: data.title,
      image: {
        '@type': 'ImageObject',
        url: getImage(data.heroImage)?.images?.fallback?.src || '',
        width: getImage(data.heroImage)?.width || 0,
        height: getImage(data.heroImage)?.height || 0,
      },
      author: {
        '@type': 'Person',
        name: config.author,
        url: config.authorUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: config.publisher,
        url: `${config.siteUrl}/blog`,
        logo: everlyLogo,
      },
      datePublished: data.publishDateISO,
      mainEntityOfPage: mainEntityOfPage,
    },
  ];
};

// FAQ Schema can have many questions and answers. These values are pulled from Contentful.
export const schemaFaqJSONLD = (faqs: FAQItem[]): SchemaFAQ | null =>
  faqs && {
    '@context': schemaContext,
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
